<svelte:options runes={false} />

<script context="module">
  // The body sections live here rather than in the markup so that App can read
  // their count when it resolves this route's four states. The copy itself has
  // not moved out of this file.
  // Rewritten 2026-08-09 in the docs/UX_NORTH_STAR.md voice, with the asking-
  // rate decision built in. The prior payer paragraph told readers to type "an
  // hourly rate at or above the invoice's floor"; there is no floor.
  //
  // U07 name rollout, 2026-08-09 (Kai, solo-gamma): the product names itself
  // Meritmark here and the closing section says where the name comes from. The
  // quantum meruit reference is deliberately framed as the *situation* the page
  // is built for and explicitly disclaimed as a claim anyone can bring -- the
  // doctrine is a litigation remedy, this is a voluntary additive contribution,
  // and copy that blurred the two would assert an obligation that does not
  // exist (and would edge into U08's counsel-only territory). Keep that
  // sentence if you edit this section.
  export const INTRO_SECTIONS = [
    {
      lead: "From a payer's standpoint:",
      body: "open the link you were sent. The work is listed — a label, a short description, the hours it took. Pick the items you want to pay toward. The operator's asking rate is filled in as a starting point; raise it or lower it as you see fit — it's an ask, not a price. Add a fixed amount on top if you like. Enter your email, pick how to pay, and check the total the server calculates for you. Your exact choices are saved before anything reaches a payment processor; from there you continue to the secure payment page, or follow the operator's manual instructions if that's what's offered. Nothing you do closes the invoice — others can keep paying toward the same work after you.",
    },
    {
      lead: "From an operator's standpoint:",
      body: "sign in, pick a project, and draft an invoice. Add work items — a label, a description, the hours — and mark each one reported when it's ready to be paid for; only reported work is visible to someone holding the link. Issue the invoice to get its shareable link, then watch contributions arrive against each item. Invoices move through draft, issued, archived, and void; archiving only takes an invoice off the open index — the direct link keeps working until the invoice is voided.",
    },
    {
      lead: 'Why it works this way:',
      body: 'most invoices assume one seller, one debtor, and one amount. This one assumes several people might want to pay for the same work, each at their own rate, without waiting for anyone to go first. Saving your exact selection and quote before touching a payment processor means the record of what you intended survives redirects, retries, and delays — and a signed webhook from the processor, not a browser redirect, is what confirms a payment actually happened.',
    },
    {
      body: "Today that means Stripe Checkout and whatever manual instructions an operator has configured. Other payment providers may appear in the catalog before they're usable — ask the operator if you're unsure whether one is live.",
    },
    {
      lead: 'Where the name comes from:',
      body: 'Meritmark — from the Latin merēre, to earn or to deserve. There is an old legal phrase for paying someone the reasonable value of work they already did when no price was ever agreed: quantum meruit, "as much as he has earned." That names the situation this page is built for. It is not a claim anyone can bring here: nothing on this page is owed, and nothing about it is enforced. The operator marks what was done. You decide what it was worth.',
    },
  ];
</script>

<script>
  import SectionHeading from '../components/SectionHeading.svelte';
  import StatRow from '../components/StatRow.svelte';
</script>

<section class="panel intro-panel">
  <SectionHeading eyebrow="How it works" title="The people who benefit decide what it's worth.">
    <svelte:fragment slot="trailing"><strong>/intro</strong></svelte:fragment>
  </SectionHeading>

  <p>
    This is Meritmark. Someone did real work, and this page turns it into an open,
    shareable invoice. The operator posts the work — labelled items, real hours —
    and states an asking rate. Anyone with the link can read what was done, choose
    what to pay toward, and set their own rate, above the ask or below it. Nothing
    fills up and nothing closes: reported work stays open to the next person who
    benefits from it.
  </p>

  <div class="stats intro-stats">
    <StatRow label="Core idea" value="reported work, your rate, additive contributions" />
    <StatRow label="Payer needs" value="just the invoice link — no account required" />
    <StatRow label="Operator needs" value="an operator account and one issued invoice" />
    <StatRow label="Payment path" value="Stripe Checkout or manual instructions, intent saved first" />
  </div>

  <div class="intro-copy">
    {#each INTRO_SECTIONS as section (section.body)}
      <p>
        <!-- The space is an expression, not template whitespace: the compiler trims
             whitespace at the {/if} boundary, so every lead ran straight into its
             body ("From a payer's standpoint:open the link..."). Pre-dated the U07
             copy and affected all four original sections. -->
        {#if section.lead}<strong>{section.lead}</strong>{' '}{/if}{section.body}
      </p>
    {/each}
  </div>
</section>
